/**
 * UserMenu — Small user indicator in the header
 * CSS prefix: um-
 *
 * When NOT logged in: shows "Sign In" button
 * When logged in: shows avatar + email with dropdown
 */

import { useState, useEffect, useRef } from 'react'
import { LogOut, User, ChevronDown } from 'lucide-react'
import { useAuth } from './AuthProvider'
import AuthModal from './AuthModal'
import './UserMenu.css'

export default function UserMenu() {
  const { user, isAuthenticated, signOut, loading, userTier } = useAuth()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const menuRef = useRef(null)

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [dropdownOpen])

  // Close dropdown on Escape
  useEffect(() => {
    if (!dropdownOpen) return
    const onKey = (e) => { if (e.key === 'Escape') setDropdownOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [dropdownOpen])

  if (loading) return null

  const handleSignOut = async () => {
    setDropdownOpen(false)
    try {
      await signOut()
    } catch {
      // Ignore sign-out errors
    }
  }

  // Get user initial for avatar
  const initial = user?.email ? user.email.charAt(0).toUpperCase() : '?'
  const displayEmail = user?.email || ''

  // Not logged in
  if (!isAuthenticated) {
    return (
      <>
        <button
          className="um-signin-btn"
          onClick={() => setShowAuthModal(true)}
        >
          Sign In
        </button>
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          initialTab="signin"
        />
      </>
    )
  }

  // Logged in
  return (
    <div className="um-container" ref={menuRef}>
      <button
        className="um-trigger"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        title={displayEmail}
      >
        <span className="um-avatar">{initial}</span>
        <span className="um-email">{displayEmail}</span>
        <ChevronDown size={12} className={`um-chevron ${dropdownOpen ? 'open' : ''}`} />
      </button>

      {dropdownOpen && (
        <div className="um-dropdown">
          <div className="um-dropdown-header">
            <span className="um-dropdown-email">{displayEmail}</span>
            <span className="um-dropdown-tier">{userTier} tier</span>
          </div>
          <div className="um-dropdown-divider" />
          <button className="um-dropdown-item" onClick={() => setDropdownOpen(false)}>
            <User size={14} /> My Account
          </button>
          <button className="um-dropdown-item um-dropdown-signout" onClick={handleSignOut}>
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      )}
    </div>
  )
}
